import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { listaPrecioService, ListaPrecio, ListaPrecioItemCreate } from '../../services/listaPrecioService';
import { productoService } from '../../services/productoService';

interface ListaPrecioDetailProps {
  lista: ListaPrecio;
  onClose: () => void;
}

const ListaPrecioDetail = ({ lista, onClose }: ListaPrecioDetailProps) => {
  const [showAddItem, setShowAddItem] = useState(false);
  const queryClient = useQueryClient();

  const { data: listaData } = useQuery(
    ['lista-precio', lista.id],
    () => listaPrecioService.getById(lista.id),
    {
      initialData: { data: { lista } }
    }
  );

  const { data: productosData } = useQuery(
    'productos',
    () => productoService.getAll(1, 200)
  );

  const deleteItemMutation = useMutation(
    (itemId: number) => listaPrecioService.deleteItem(lista.id, itemId),
    {
      onSuccess: () => {
        toast.success('Item eliminado correctamente');
        queryClient.invalidateQueries(['lista-precio']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al eliminar item');
      }
    }
  );

  const handleDeleteItem = (itemId: number) => {
    if (window.confirm('¿Estás seguro de eliminar este item?')) {
      deleteItemMutation.mutate(itemId);
    }
  };

  const listaCompleta = listaData?.data.lista || lista;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Lista de Precios: {listaCompleta.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 flex justify-between items-center">
          <span className={`px-2 py-1 text-xs rounded-full ${listaCompleta.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {listaCompleta.isActive ? 'Activa' : 'Inactiva'}
          </span>
          <button
            onClick={() => setShowAddItem(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            + Agregar Producto
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variante</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio Base</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio Lista</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Diferencia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {listaCompleta.items?.map((item) => {
                const precioBase = Number(item.producto?.precioBase || 0);
                const diferencia = Number(item.price) - precioBase;
                return (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.producto?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.variante?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${precioBase.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${Number(item.price).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={diferencia !== 0 ? (diferencia > 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-600'}>
                        {diferencia > 0 ? '+' : ''}${diferencia.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {listaCompleta.items?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No hay productos en esta lista
          </div>
        )}

        {showAddItem && (
          <AddItemForm
            listaId={lista.id}
            productos={productosData?.data.productos || []}
            onClose={() => {
              setShowAddItem(false);
              queryClient.invalidateQueries(['lista-precio']);
            }}
          />
        )}
      </div>
    </div>
  );
};

interface AddItemFormProps {
  listaId: number;
  productos: any[];
  onClose: () => void;
}

const AddItemForm = ({ listaId, productos, onClose }: AddItemFormProps) => {
  const [productoId, setProductoId] = useState<number | undefined>(undefined);
  const [varianteId, setVarianteId] = useState<number | undefined>(undefined);
  const [price, setPrice] = useState<string>('');
  const queryClient = useQueryClient();

  const productoSeleccionado = productos.find(p => p.id === productoId);

  const addItemMutation = useMutation(
    (data: ListaPrecioItemCreate) => listaPrecioService.addItem(listaId, data),
    {
      onSuccess: () => {
        toast.success('Producto agregado a la lista');
        queryClient.invalidateQueries(['lista-precio']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al agregar producto');
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!productoId || !price) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    addItemMutation.mutate({
      productoId,
      varianteId,
      price: parseFloat(price)
    });
  };

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded">
      <h3 className="font-semibold mb-3">Agregar Producto</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Producto *
          </label>
          <select
            value={productoId || ''}
            onChange={(e) => {
              setProductoId(e.target.value ? Number(e.target.value) : undefined);
              setVarianteId(undefined);
            }}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Selecciona producto</option>
            {productos.map((producto) => (
              <option key={producto.id} value={producto.id}>
                {producto.name} - ${typeof producto.precioBase === 'number' ? producto.precioBase.toFixed(2) : (Number(producto.precioBase) || 0).toFixed(2)}
              </option>
            ))}
          </select>
        </div>

        {productoSeleccionado?.variantes && productoSeleccionado.variantes.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Variante (opcional)
            </label>
            <select
              value={varianteId || ''}
              onChange={(e) => setVarianteId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Sin variante</option>
              {productoSeleccionado.variantes.map((variante: any) => (
                <option key={variante.id} value={variante.id}>
                  {variante.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Precio *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder={productoSeleccionado ? `Precio base: $${typeof productoSeleccionado.precioBase === 'number' ? productoSeleccionado.precioBase.toFixed(2) : (Number(productoSeleccionado.precioBase) || 0).toFixed(2)}` : ''}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={addItemMutation.isLoading}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {addItemMutation.isLoading ? 'Agregando...' : 'Agregar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ListaPrecioDetail;

